import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Header';
import { BottomNav } from '../components/BottomNav';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Star, MapPin, Flag, SealCheck, WhatsappLogo } from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import { formatDualCurrency } from '../utils/currency';
import { useCurrency } from '../context/CurrencyContext';
import { getCategoryName, getSubcategoryName } from '../utils/categoryNames';
import { ListingStatusBadge } from '../components/ListingStatusBadge';
import { MessagesList } from '../components/messages/MessagesList';
import { TopSellerBadge } from '../components/TopSellerBadge';
import { SellerName } from '../components/SellerName';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const UserProfilePage = () => {
  const { userId } = useParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { secondaryCurrency } = useCurrency();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('reviews');
  const [categoryFilter, setCategoryFilter] = useState('__all__');
  // Profile-tab unread badge — bubbled up from <MessagesList> so we keep
  // a single source of truth for thread fetching.
  const [totalUnread, setTotalUnread] = useState(0);

  const fetchProfile = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/users/${userId}/profile`);
      setProfile(data);
    } catch {
      toast.error('Profile not found');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Privacy: only the logged-in user sees their own message threads.
  const isOwnProfile = user?.user_id === userId;

  // Unique categories represented by this seller's listings, used to populate
  // the filter dropdown. Only non-empty categories appear (no subcategories).
  const availableCategories = useMemo(() => {
    if (!profile) return [];
    const set = new Set();
    (profile.active_listings || []).forEach((l) => l.category && set.add(l.category));
    // Include categories that only appear via snapshot reviews too, so the
    // filter still lets users scope reviews for sold/deleted listings.
    (profile.reviews || []).forEach((r) => r.category && set.add(r.category));
    return Array.from(set);
  }, [profile]);

  // Reset the filter if the current selection disappears (e.g. listing deleted)
  useEffect(() => {
    if (categoryFilter !== '__all__' && !availableCategories.includes(categoryFilter)) {
      setCategoryFilter('__all__');
    }
  }, [availableCategories, categoryFilter]);

  const filteredListings = useMemo(() => {
    if (!profile) return [];
    if (categoryFilter === '__all__') return profile.active_listings || [];
    return (profile.active_listings || []).filter((l) => l.category === categoryFilter);
  }, [profile, categoryFilter]);

  const filteredReviews = useMemo(() => {
    if (!profile) return [];
    if (categoryFilter === '__all__') return profile.reviews || [];
    return (profile.reviews || []).filter((r) => r.category === categoryFilter);
  }, [profile, categoryFilter]);

  const handleReportReview = async (reviewId) => {
    try {
      await axios.post(`${API}/reviews/report`, { review_id: reviewId, reason: 'Inappropriate content' }, { withCredentials: true });
      toast.success(t('review.reviewReported'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to report');
    }
  };

  const memberSinceDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream">
        <Header />
        <div className="text-center py-12">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-text_muted">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className="min-h-screen bg-cream pb-20 md:pb-0">
      <Header />
      <div className="max-w-5xl mx-auto p-6">
        {/* Profile Card */}
        <div className="bg-white border border-border rounded-2xl p-6 md:p-8 mb-6" data-testid="profile-card">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 sm:gap-6">
            {/* Left column — avatar */}
            <div className="flex-shrink-0">
              <div className="w-24 h-24 rounded-full overflow-hidden">
                {profile.picture && profile.picture !== 'None' ? (
                  <img src={profile.picture.startsWith('http') ? profile.picture : `${API}/files/${profile.picture}`} alt={profile.name} className="w-24 h-24 rounded-full object-cover" />
                ) : (
                  <div className="w-24 h-24 bg-primary rounded-full flex items-center justify-center text-white text-3xl font-bold">
                    {profile.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                )}
              </div>
            </div>

            {/* Right column — name, rating, location text, WhatsApp, bio, role */}
            <div className="flex-1 min-w-0 w-full text-center sm:text-left">
              <div
                className="flex items-center gap-2 flex-wrap justify-center sm:justify-start"
                data-testid="profile-name"
              >
                <SellerName
                  user={profile}
                  variant="stack"
                  badgeSize="inline"
                  primaryClassName="text-lg md:text-xl font-medium text-text_strong tracking-wide"
                  secondaryClassName="text-xs text-text_muted"
                  testIdPrefix="profile-display"
                />
                {profile.is_verified && <SealCheck size={22} weight="fill" className="text-primary" />}
                <TopSellerBadge badge={profile.top_category_badge} />
                {profile.sold_count > 0 && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-success-soft text-success whitespace-nowrap"
                    title={`${profile.sold_count} ${t('profile.soldCountTitle')}`}
                    data-testid="sold-count-badge"
                  >
                    ✅ {profile.sold_count} {t(profile.sold_count === 1 ? 'profile.soldCount_one' : 'profile.soldCount_other')}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 mt-1 justify-center sm:justify-start" data-testid="profile-rating">
                <Star size={18} weight="fill" className="text-warning" />
                <span className="font-semibold text-text_main">{profile.average_rating}</span>
                <span className="text-text_muted text-sm">({profile.total_reviews} {t('review.reviews')})</span>
              </div>
              <p className="text-sm text-text_muted mt-1">{t('review.memberSince')}: {memberSinceDate(profile.created_at)}</p>
              {(profile.region || profile.city) && (
                <p className="text-sm text-text_muted flex items-center gap-1 mt-1 justify-center sm:justify-start">
                  <MapPin size={14} /> {[profile.city, profile.region].filter(Boolean).join(', ')}
                </p>
              )}
              {profile.whatsapp_enabled && profile.phone && (
                <a
                  href={`https://wa.me/${profile.phone.replace(/[^\d]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-2 px-2.5 py-1 rounded-full text-xs font-medium bg-success-soft text-success border border-success hover:bg-success-soft transition-colors"
                  data-testid="profile-whatsapp-badge"
                >
                  <WhatsappLogo size={13} weight="fill" /> WhatsApp
                </a>
              )}
              {profile.bio && (
                <p className="text-sm text-text_main mt-3 whitespace-pre-line" data-testid="profile-bio">
                  {profile.bio}
                </p>
              )}
              {profile.role && profile.role !== 'user' && (
                <div className="mt-1.5">
                  <span className={`inline-block text-xs font-medium px-2.5 py-0.5 rounded-full border capitalize ${
                    profile.role === 'admin' ? 'bg-danger-soft text-danger border-danger' : 'bg-purple-50 text-purple-700 border-purple-200'
                  }`} data-testid="user-role-badge">
                    {profile.role}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Review flow removed from the profile page — reviews are now
            created exclusively from the ListingDetailPage after the user has
            interacted (messaged) with the seller. Display below is read-only. */}

        {/* Tabs */}
        <div className="flex flex-wrap items-center gap-4 border-b border-border mb-6">
          <div className="flex" data-testid="profile-tabs">
          <button
            onClick={() => setActiveTab('reviews')}
            className={`px-5 py-3 text-sm font-medium transition-colors relative ${
              activeTab === 'reviews' ? 'text-primary' : 'text-text_muted hover:text-text_main'
            }`}
            data-testid="tab-reviews"
          >
            <span className="capitalize">{t('review.reviews')}</span> ({profile.total_reviews})
            {activeTab === 'reviews' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
          </button>
          <button
            onClick={() => setActiveTab('listings')}
            className={`px-5 py-3 text-sm font-medium transition-colors relative ${
              activeTab === 'listings' ? 'text-primary' : 'text-text_muted hover:text-text_main'
            }`}
            data-testid="tab-listings"
          >
            {t('profile.listings')} ({profile.active_listings.length})
            {activeTab === 'listings' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
          </button>
          {isOwnProfile && (
            <button
              onClick={() => setActiveTab('messages')}
              className={`px-5 py-3 text-sm font-medium transition-colors relative inline-flex items-center gap-1.5 ${
                activeTab === 'messages' ? 'text-primary' : 'text-text_muted hover:text-text_main'
              }`}
              data-testid="tab-messages"
            >
              <span>{t('messages.messages')}</span>
              {totalUnread > 0 && (
                <span
                  className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold leading-none text-white bg-primary rounded-full"
                  data-testid="tab-messages-unread"
                >
                  {totalUnread > 99 ? '99+' : totalUnread}
                </span>
              )}
              {activeTab === 'messages' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
            </button>
          )}
          </div>
          {/* Category filter — only shown when the reviews/listings tabs are
              active AND the seller has listings in more than one category. */}
          {activeTab !== 'messages' && availableCategories.length > 1 && (
            <div className="ml-auto pb-2 sm:pb-0">
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="h-9 text-sm min-w-[170px]" data-testid="profile-category-filter">
                  <SelectValue placeholder={t('profile.category')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__" data-testid="profile-category-all">
                    {t('profile.allCategories')}
                  </SelectItem>
                  {availableCategories.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`profile-category-${c}`}>
                      {getCategoryName(c, t)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Listings Tab */}
        {activeTab === 'listings' && (
          <div data-testid="user-listings-section">
            {filteredListings.length === 0 ? (
              <div className="bg-white border border-border rounded-2xl p-8 text-center">
                <p className="text-text_muted">
                  {categoryFilter === '__all__' ? t('profile.noListings') : t('profile.noListingsInCategory')}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {filteredListings.map((listing) => {
                  const price = formatDualCurrency(listing.price_mga, secondaryCurrency);
                  return (
                    <div key={listing.listing_id} className="listing-card border border-border bg-white rounded-xl overflow-hidden" data-testid={`listing-${listing.listing_id}`}>
                      <Link to={`/listings/${listing.listing_id}`}>
                        <div className="aspect-[4/3] bg-surface">
                          {listing.images?.[0] ? (
                            <img src={`${API}/files/${listing.images[0]}`} alt={listing.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-text_muted">{t('common.noImage')}</div>
                          )}
                        </div>
                      </Link>
                      <div className="p-4">
                        <Link to={`/listings/${listing.listing_id}`}>
                          <h3 className="font-semibold font-heading text-sm mb-1 truncate hover:text-primary">{listing.title}</h3>
                        </Link>
                        <p className="text-text_muted text-xs mb-2">
                          {listing.city} <span className="font-semibold text-primary">• {getCategoryName(listing.category, t)}</span>
                        </p>
                        <p className="text-lg font-bold text-primary">{price.mga}</p>
                        <p className="text-xs text-text_muted">{price.fmg}</p>
                        <div className="mt-1">
                          <ListingStatusBadge listing={listing} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div className="bg-white border border-border rounded-2xl p-6 md:p-8" data-testid="reviews-section">
            {filteredReviews.length === 0 ? (
              <p className="text-text_muted text-center py-4">
                {categoryFilter === '__all__' ? t('review.noReviews') : t('review.noReviewsInCategory')}
              </p>
            ) : (
              <div className="space-y-4">
                {filteredReviews.map((review) => (
                  <div key={review.review_id} className="border-b border-border pb-4 last:border-0" data-testid={`review-${review.review_id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm">{review.reviewer_name}</span>
                        <div className="flex">
                          {[1,2,3,4,5].map(s => (
                            <Star key={s} size={14} weight={review.rating >= s ? 'fill' : 'regular'} className={review.rating >= s ? 'text-warning' : 'text-text_muted'} />
                          ))}
                        </div>
                      </div>
                      {user && user.user_id !== review.reviewer_id && (
                        <button onClick={() => handleReportReview(review.review_id)} className="text-text_muted hover:text-danger" title={t('review.reportReview')}>
                          <Flag size={14} />
                        </button>
                      )}
                    </div>
                    {/* Snapshot context — listing title + category/subcategory.
                        Skipped gracefully on legacy reviews without snapshot. */}
                    {(review.listing_title || review.category) && (
                      <div className="flex flex-wrap items-center gap-2 mb-1.5" data-testid={`review-context-${review.review_id}`}>
                        {review.listing_title && (
                          <span className="text-xs text-text_muted italic truncate max-w-[260px]" title={review.listing_title}>
                            « {review.listing_title} »
                          </span>
                        )}
                        {review.category && (
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                            {getCategoryName(review.category, t)}
                            {review.subcategory ? ` · ${getSubcategoryName(review.subcategory, t)}` : ''}
                          </span>
                        )}
                      </div>
                    )}
                    {review.comment && <p className="text-sm text-text_main">{review.comment}</p>}
                    <p className="text-xs text-text_muted mt-1">{formatDistanceToNow(new Date(review.created_at), { addSuffix: true })}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Messages Tab — only visible on own profile */}
        {activeTab === 'messages' && isOwnProfile && (
          <div data-testid="user-messages-section">
            <MessagesList mode="embedded" onUnreadChange={setTotalUnread} />
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
};
